import { Component, EventEmitter, inject, Input, OnDestroy, OnInit, Output } from "@angular/core";
import { AbstractControl, FormGroup, ReactiveFormsModule } from "@angular/forms";
import { LayoutAlignDirective, LayoutDirective } from "@ngbracket/ngx-layout/flex";
import { ForgeButtonModule, ForgeCircularProgressModule } from "@tylertech/forge-angular";
import { LgdFormControlFactory, LgdPasswordComponent, LgdTextComponent, setEnabled } from "@tylertech/lgd-reactive-forms";
import { getLoggerFor } from "@tylertech/logging";
import { StringHelper } from "@tylertech/shared-module";
import { ExecutionResult, Token } from "@tylertech/tyler-api-types";
import { catchError, map, of, Subject } from "rxjs";
import { Auth0ErrorResponse, LoginView } from "../../../interfaces";
import { isErroredExecutionResult, LoggingService, LoginError, OpenIdService, RouteInfoService, SecurityService } from "../../../services";
import { SecurityScreenType } from "../securityScreenType";

@Component({
    selector: "migrate-tid-form",
    templateUrl: "./migrateTidForm.tmpl.html",
    styleUrls: [
        "./migrateTidForm.component.scss",
    ],
    imports: [
        LayoutAlignDirective,
        LayoutDirective,
        ForgeButtonModule,
        ForgeCircularProgressModule,
        LgdPasswordComponent,
        LgdTextComponent,
        ReactiveFormsModule
    ]
})
export class MigrateTidFormComponent implements OnInit, OnDestroy
{
    private readonly $loggingService = inject(LoggingService);
    private readonly $routeInfoService = inject(RouteInfoService);
    private readonly $securityService = inject(SecurityService);
    private readonly $openIdService = inject(OpenIdService);

    @Input() private readonly isDisabled: boolean;
    @Input() readonly showForgotPwLink: boolean;

    @Output() protected readonly goto = new EventEmitter<SecurityScreenType>();

    private readonly log = getLoggerFor("MigrateTidFormComponent");
    protected readonly destructor$ = new Subject<void>();
    private _errors: Record<string, string>;

    readonly mainForm: FormGroup;

    error: string;
    loading: boolean;

    constructor()
    {
        this.mainForm = new FormGroup({
            username: LgdFormControlFactory.text(undefined, true, {}),
            password: LgdFormControlFactory.text(undefined, true, {}),
        });
    }

    protected get usernameControl(): AbstractControl<string> { return this.mainForm.get("username"); }
    protected get passwordControl(): AbstractControl<string> { return this.mainForm.get("password"); }

    private get tenant(): string
    {
        return this.$routeInfoService.tenant?.replace(/"/g, "");
    }

    get Errors(): Record<string, string> { return this._errors; }

    get submitDisabled(): boolean
    {
        return this.isDisabled || this.loading;
    }

    get forgotPasswordDisabled(): boolean { return this.isDisabled || this.loading; }

    ngOnDestroy(): void
    {
        this.destructor$.next();
        this.destructor$.complete();
    }

    ngOnInit(): void
    {
        // TODO - why do we ever have quotes in the username value?
        const username = this.$securityService.Username?.replace(/"/g, "");
        this.usernameControl.setValue(username?.replace(/"/g, ""));
    }

    SwitchToForgotPassword(): void
    {
        this.goto.emit(SecurityScreenType.ForgotPassword);
    }

    onSubmit(): void
    {

        if (this.mainForm.invalid)
        {
            return;
        }

        this.loading = true;
        setEnabled(this.mainForm, true);

        this.$openIdService.populateToken()
            .then(_ => this.onSubmitImpl());
    }

    onSubmitImpl(): void
    {
        const credentials: LoginView = {
            Tenant: this.tenant,
            Username: this.usernameControl.value,
            Password: this.passwordControl.value,
            MigrateTidUser: true,
            Token: {
                access_token: this.$openIdService.getToken()
            } as Token,
        };
        this.$securityService.loginWithRedirect(credentials)
            .pipe(
                // TODO - we shouldn't ever receive anything back from login other than an error...
                map((_success: boolean) =>
                {
                    console.debug("successful login");
                    // // location.reload();
                    // // TODO - this component shouldn't being doing this. the end of a successful login process should redirect on it's own.
                    // if (this.$securityService.isAuthenticated)
                    // {
                    //     this.$securityService.returnToDesiredRoute();
                    // }
                }),
                catchError((result: unknown) =>
                {
                    this.$loggingService.error("error logging in");
                    if (result instanceof LoginError)
                    {
                        const rejectedResponse = result.response;
                        const executionResult = rejectedResponse.body;
                        this.error = this.parseError(executionResult);
                        return of();
                    }
                    else if (isErroredExecutionResult(result))
                    {
                        this.error = this.parseError(result);
                        return of();
                    }

                    this.$loggingService.info("result:", result);
                    return of();
                }))
            .subscribe({
                complete: () =>
                {
                    setEnabled(this.mainForm, !this.isDisabled);

                    this.loading = false;
                }
            });
    }

    private parseError<T = unknown>(result: ExecutionResult<T>): string
    {
        const message = result.ErrorMessage
            ?? result.ValidationMessage;
        const errorObj = StringHelper.parse<Auth0ErrorResponse>(message);
        return errorObj?.error_description
            ?? message;
    }
}
